import { useEffect, useRef } from 'react';

/**
 * WYSIWYG gallery content renders a <a> tag with a Wordpress href attribute.
 * We replace the href with the largest image size, so on click the user can
 * see the image in a new tab. If for some reason the srcset is empty, we disable
 * the link wrapping the image.
 */
const useReplaceWPGalleryLinks = () => {
  const ref = useRef(null);

  /**
   * Disable link if we cannot find a suitable href
   * @param link The link wrapping the gallery image
   */
  const disableLink = (link: HTMLLinkElement) => {
    link.removeAttribute('href');
    link.setAttribute('aria-disabled', 'true');
    link.style.pointerEvents = 'none';
  };

  /**
   * If the image has been processed by gatsby image, find the largest src and use it as href
   * @param link The link wrapping the gallery image
   * @param gatsbyImage The gallery image
   */
  const handleGatsbyImage = (link: HTMLLinkElement, gatsbyImage: Element) => {
    // depending on the environment, it can be one of the following
    const srcset =
      gatsbyImage?.getAttribute('srcset') ??
      gatsbyImage?.getAttribute('data-srcset');
    /* match the last part of the srcset, before the first comma is found
     * it can be an issue if the image url contains a comma, but it's not supposed
     * to happen with gatsby-source-wordpress
     */
    const largerSrcSet = srcset?.match(/([^,]*)(\s[0-9]*)w$/)?.[0];
    // if we find a larger src set, we use it as a href to make the image clickable, else we disable the link
    if (largerSrcSet) {
      link.href = largerSrcSet;
      link.setAttribute('target', '_blank');
    } else {
      disableLink(link);
    }
  };

  /**
   * If the image has not been processed by gatsby image, get the src of the img tag
   * @param link The link wrapping the gallery image
   * @param baseImage The gallery image
   */
  const handleBaseImage = (link: HTMLLinkElement, baseImage: HTMLElement) => {
    const src = baseImage?.getAttribute('src');
    link.href = src;
    link.setAttribute('target', '_blank');
  };

  useEffect(() => {
    const innerContent = ref.current;
    const links: HTMLLinkElement[] =
      innerContent.querySelectorAll('.gallery-item a');
    links.forEach((link) => {
      /* depending on how the inline image has been parsed by Gatsby (logic to be understood yet...)
      /* the gallery image can be a single img tag or a more complex markup */
      const gatsbyImage =
        link.querySelector('img[data-main-image]');
      const baseImage = link.querySelector('img');
      if (gatsbyImage) {
        handleGatsbyImage(link, gatsbyImage);
      } else if (baseImage) {
        handleBaseImage(link, baseImage);
      } else {
        disableLink(link);
      }
    });
  }, []);
  return ref;
};

export default useReplaceWPGalleryLinks;
